import React, { FC, useEffect, useRef, useState } from "react";
import {
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
  ThemeProvider,
  createTheme,
} from "@mui/material";
import createPalette from "@mui/material/styles/createPalette";
import isEqual from "lodash/isEqual";

import { vars } from "../Variables";
import { useField } from "formik";

const { primaryFont, primaryColor, errorColor, greyLight, grey, greyDark } =
  vars;

declare module "@mui/material/styles" {
  interface Theme {}
  // allow configuration using `createTheme`
  interface ThemeOptions {}
}

const customPalette = createPalette({
  primary: {
    main: primaryColor,
  },
  grey: {
    "100": greyLight,
    "200": grey,
    "300": greyDark,
  },
  error: {
    main: errorColor,
  },
});

const theme = createTheme({
  components: {
    MuiFormLabel: {
      styleOverrides: {
        asterisk: {
          color: customPalette.error.main,
        },
        root: {
          fontFamily: primaryFont,
          fontWeight: 400,
          letterSpacing: "normal",
          lineHeight: "143%",
          fontSize: "1rem",
          color: customPalette?.grey[100],

          "&.Mui-focused": {
            color: customPalette?.grey[100],
          },
        },
      },
    },
    MuiFormGroup: {
      styleOverrides: {
        root: {
          marginTop: 10,
          columnGap: 20,
          rowGap: 10,
        },
      },
    },
    MuiFormControlLabel: {
      styleOverrides: {
        label: {
          fontFamily: primaryFont,
          fontWeight: 500,
          height: 33,
          fontSize: "13px",
          lineHeight: "108%",
          color: "#161458",
          border: "1px solid #161458",
          borderRadius: "50px",
          minWidth: 113,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          transition: "all ease-in-out .3s",
        },
        root: {
          margin: 0,
        },
      },
    },
    MuiFormControl: {
      styleOverrides: {
        root: {
          // minWidth: 113,
        },
      },
    },
    MuiRadio: {
      styleOverrides: {
        root: {
          display: "none",
          "&.Mui-checked": {
            "& + .MuiFormControlLabel-label": {
              backgroundColor: "#B1B3EA",
            },
          },
        },
      },
    },
  },
});

type itemObj = {
  label: string;
  value: string;
};

interface ICustomRadio {
  label?: string;
  name: string;
  id: number | string;
  value?: number | string;
  setValue?: Function;
  onChange?: (value: string | number) => void;
  items: itemObj[];
  fullWidth?: boolean;
  row?: boolean;
  sx?: object;
}

export const CustomRadio: FC<ICustomRadio> = ({
  items,
  label,
  id,
  name,
  setValue,
  onChange,
  fullWidth = true,
  row = true,
  sx,
}) => {
  const [field, , helpers] = useField({ name });

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    helpers.setValue(event.target.value);
    onChange?.(event.target.value);
    setValue?.(event.target.value);
  };

  return (
    <ThemeProvider theme={theme}>
      <FormControl sx={sx} fullWidth={fullWidth} required>
        {label && <FormLabel id={`id-${id}`}>{label}</FormLabel>}
        <RadioGroup
          row={row}
          aria-labelledby={`id-${id}`}
          name={name}
          value={field.value}
          onChange={handleChange}
        >
          {items?.map((item: itemObj) => {
            return (
              <FormControlLabel
                key={item.value}
                value={item.value}
                control={<Radio />}
                label={item.label}
              />
            );
          })}
        </RadioGroup>
      </FormControl>
    </ThemeProvider>
  );
};
